
import React, { useEffect, useState } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { API_BASE_URL } from '../config'; 
import { Trophy, Medal, RefreshCw, Car, Users, TrendingUp, AlertOctagon, ArrowLeftRight, CheckCircle, X, Swords, History, ChevronDown } from 'lucide-react';
import * as ReactRouterDOM from 'react-router-dom';
import { useLeague } from '../components/LeagueContext';
import { Season } from '../types';
import { PageHero } from '../components/PageHero';
import { hasActiveSeason, NoActiveSeason } from '../components/NoActiveSeason';

const { Link } = ReactRouterDOM;

interface DriverStanding {
    id: string; username: string; teamName: string; teamColor: string; teamLogo?: string;
    totalPoints: number; lastRacePoints: number; penaltyPoints: number; wins: number; podiums: number; races: number;
}
interface ConstructorStanding {
    id: string; name: string; teamColor: string; teamLogo?: string; carImageUrl?: string;
    totalPoints: number; lastRacePoints: number; wins: number;
}

// ─── COMPARISON MODAL ────────────────────────────────────────
const ComparisonModal: React.FC<{ item1: any; item2: any; type: 'driver' | 'team'; onClose: () => void }> = ({ item1, item2, type, onClose }) => {
    const c1 = type === 'driver' ? (item1.teamColor || '#666') : (item1.teamColor || '#666');
    const c2 = type === 'driver' ? (item2.teamColor || '#666') : (item2.teamColor || '#666');

    const CompRow = ({ label, v1, v2, invert = false }: { label: string; v1: number; v2: number; invert?: boolean }) => {
        const win1 = invert ? v1 < v2 : v1 > v2;
        const win2 = invert ? v2 < v1 : v2 > v1;
        const max = Math.max(v1, v2) * 1.2 || 1;
        return (
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center py-3 border-b border-gray-800/30">
                <div className="flex items-center justify-end gap-3">
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden w-20 md:w-28">
                        <div className="h-full rounded-full float-right transition-all duration-500" style={{ width: `${(v1 / max) * 100}%`, backgroundColor: c1 }} />
                    </div>
                    <span className={`font-mono font-bold text-base w-8 text-right ${win1 ? 'text-white' : 'text-gray-600'}`}>{v1}</span>
                </div>
                <div className="text-[9px] uppercase font-bold text-gray-500 w-20 text-center tracking-wider">{label}</div>
                <div className="flex items-center justify-start gap-3">
                    <span className={`font-mono font-bold text-base w-8 ${win2 ? 'text-white' : 'text-gray-600'}`}>{v2}</span>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden w-20 md:w-28">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(v2 / max) * 100}%`, backgroundColor: c2 }} />
                    </div>
                </div>
            </div>
        );
    };

    const rows = type === 'driver' ? [
        { label: 'Punkte', v1: Math.round(item1.totalPoints), v2: Math.round(item2.totalPoints) },
        { label: 'Siege', v1: item1.wins, v2: item2.wins },
        { label: 'Podien', v1: item1.podiums, v2: item2.podiums },
        { label: 'Rennen', v1: item1.races, v2: item2.races },
        { label: 'Strafen', v1: item1.penaltyPoints || 0, v2: item2.penaltyPoints || 0, invert: true },
    ] : [
        { label: 'Punkte', v1: Math.round(item1.totalPoints), v2: Math.round(item2.totalPoints) },
        { label: 'Siege', v1: item1.wins, v2: item2.wins },
    ];

    const name1 = type === 'driver' ? item1.username : item1.name;
    const name2 = type === 'driver' ? item2.username : item2.name;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-[#12121a] border border-gray-800/60 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="h-[2px]" style={{ background: `linear-gradient(90deg, ${c1}, transparent 30%, transparent 70%, ${c2})` }} />
                <div className="p-6">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-2">
                            <Swords size={16} className="text-yellow-500" />
                            <span className="text-[11px] font-black uppercase tracking-[0.15em] text-white/90">Head to Head</span>
                        </div>
                        <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors"><X size={18} /></button>
                    </div>

                    {/* Names */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-8 rounded-sm" style={{ backgroundColor: c1 }} />
                            <span className="text-lg font-black italic uppercase text-white">{name1}</span>
                        </div>
                        <div className="text-[10px] font-black text-gray-600 bg-gray-800/40 px-3 py-1 rounded-full">VS</div>
                        <div className="flex items-center gap-3">
                            <span className="text-lg font-black italic uppercase text-white">{name2}</span>
                            <div className="w-3 h-8 rounded-sm" style={{ backgroundColor: c2 }} />
                        </div>
                    </div>

                    {/* Rows */}
                    <div className="bg-[#0e0e16] rounded-lg p-4 border border-gray-800/30">
                        {rows.map((r, i) => <CompRow key={i} {...r} />)}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── POSITION BADGE ──────────────────────────────────────────
const PosBadge: React.FC<{ pos: number; compareMode?: boolean; isSelected?: boolean }> = ({ pos, compareMode, isSelected }) => {
    if (compareMode && isSelected) return <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />;
    if (pos === 1) return <div className="w-7 h-7 rounded-md bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center mx-auto shadow-lg shadow-yellow-500/20"><Trophy size={14} className="text-black" /></div>;
    if (pos === 2) return <div className="w-7 h-7 rounded-md bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center mx-auto"><span className="text-[11px] font-black text-black">2</span></div>;
    if (pos === 3) return <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-400 to-orange-700 flex items-center justify-center mx-auto"><span className="text-[11px] font-black text-black">3</span></div>;
    return <span className="text-sm font-mono font-bold text-gray-600">{pos}</span>;
};

// ─── MAIN COMPONENT ──────────────────────────────────────────
export const Standings: React.FC = () => {
    const { seasons, settings, isLoading: leagueLoading } = useLeague();
    const [tab, setTab] = useState<'drivers' | 'constructors'>('drivers');
    const [data, setData] = useState<{ drivers: DriverStanding[]; constructors: ConstructorStanding[]; seasonName?: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [seasons2, setSeasons2] = useState<Season[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState<string>('current');
    const [compareMode, setCompareMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => {
        apiRequest<Season[]>('seasons.php?all=true')
            .then(res => setSeasons2(Array.isArray(res) ? res : []))
            .catch(console.error);
    }, []);

    useEffect(() => {
        setLoading(true);
        const query = selectedSeasonId === 'current' ? '' : `?seasonId=${selectedSeasonId}`;
        apiRequest<{ drivers: DriverStanding[]; constructors: ConstructorStanding[]; seasonName?: string }>(`standings.php${query}`)
            .then(res => {
                if (res && res.drivers) {
                    res.drivers = res.drivers.map(d => ({ ...d, totalPoints: Number(d.totalPoints || 0), lastRacePoints: Number(d.lastRacePoints || 0) }));
                }
                if (res && res.constructors) {
                    res.constructors = res.constructors.map(c => ({ ...c, totalPoints: Number(c.totalPoints || 0), lastRacePoints: Number(c.lastRacePoints || 0) }));
                }
                setData(res);
                setError(null);
            })
            .catch(err => { console.error(err); setError("Keine Saisondaten"); })
            .finally(() => setLoading(false));
    }, [selectedSeasonId]);

    const getImageUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        if (baseUrl && !url.startsWith('/')) return `${baseUrl}/${url}`;
        if (baseUrl) return `${baseUrl}${url}`;
        return url;
    };

    const handleRowClick = (id: string) => {
        if (compareMode) {
            if (selectedIds.includes(id)) setSelectedIds(prev => prev.filter(i => i !== id));
            else if (selectedIds.length < 2) setSelectedIds(prev => [...prev, id]);
        }
    };

    const finishedSeasons = seasons.filter(s => s.status === 'FINISHED');

    if (!leagueLoading && !hasActiveSeason(seasons)) {
        return (
            <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-16">
                <PageHero
                    eyebrow="Saisonpause"
                    title="Weltmeisterschaft"
                    description="Fahrer- und Konstrukteurswertung werden mit dem Start der nächsten Saison wieder aktuell geführt."
                    icon={Trophy}
                    accent="#e10600"
                    stats={[{ label: 'Aktive Saison', value: '–' }, { label: 'Fahrer', value: 0 }, { label: 'Teams', value: 0 }]}
                />
                <NoActiveSeason description="Es gibt momentan keine laufende WM-Tabelle. Die finalen Fahrer- und Konstrukteursstände aller abgeschlossenen Saisons findest du in der Historie." />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-16">
            <PageHero
                eyebrow={data?.seasonName || 'Offizielle Saisonstände'}
                title="Weltmeisterschaft"
                description="Fahrer- und Konstrukteurswertung mit Trends, direkten Profilen und einem Vergleich für die engsten Duelle."
                icon={Trophy}
                accent="#e10600"
                stats={[
                    { label: 'Fahrer', value: data?.drivers.length || 0 },
                    { label: 'Teams', value: data?.constructors.length || 0 },
                    { label: 'Archiv', value: finishedSeasons.length },
                ]}
                actions={(
                    <div className="flex items-center gap-2 rounded-[1.1rem] border border-white/[0.08] bg-black/25 px-4 py-3">
                        <History size={14} className="text-gray-600" />
                        <select
                            className="bg-transparent text-[11px] font-bold text-white outline-none cursor-pointer uppercase tracking-wide"
                            value={selectedSeasonId}
                            onChange={e => setSelectedSeasonId(e.target.value)}
                        >
                            <option value="current">Aktuelle Saison</option>
                            {finishedSeasons.length > 0 && (
                                <optgroup label="Archiv">
                                    {finishedSeasons.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year})</option>)}
                                </optgroup>
                            )}
                        </select>
                    </div>
                )}
            />

            {loading ? (
                <div className="py-32 text-center flex flex-col items-center gap-4">
                    <div className="relative w-14 h-14">
                        <div className="absolute inset-0 border-2 border-gray-800 rounded-full" />
                        <div className="absolute inset-0 border-2 border-t-f1-red rounded-full animate-spin" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-600 animate-pulse">Lade Tabelle...</span>
                </div>
            ) : (!data || (data.drivers.length === 0 && data.constructors.length === 0)) ? (
                <div className="flex flex-col items-center justify-center py-32 text-center">
                    <Trophy className="w-16 h-16 text-gray-800 mb-4" />
                    <h3 className="text-xl font-black text-gray-500 uppercase italic mb-2">Keine Daten</h3>
                    <p className="text-gray-600 text-sm mb-6">Für diese Saison liegen noch keine Ergebnisse vor.</p>
                </div>
            ) : (
                <>
                    {/* ── TABS & ACTIONS ──────────────────────── */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-3">
                        <div className="flex w-full items-center gap-1 rounded-[1.35rem] border border-white/[0.075] bg-[#0e1017] p-1.5 md:w-auto">
                            <button
                                onClick={() => { setTab('drivers'); setCompareMode(false); setSelectedIds([]); }}
                                className={`flex flex-1 items-center justify-center gap-2 rounded-[1rem] px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] transition-all md:flex-none ${tab === 'drivers' ? 'bg-f1-red text-white shadow-lg shadow-f1-red/20' : 'text-gray-600 hover:text-gray-400'}`}
                            >
                                <Users size={14} /> Fahrer-WM
                            </button>
                            <button
                                onClick={() => { setTab('constructors'); setCompareMode(false); setSelectedIds([]); }}
                                className={`flex flex-1 items-center justify-center gap-2 rounded-[1rem] px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] transition-all md:flex-none ${tab === 'constructors' ? 'bg-f1-red text-white shadow-lg shadow-f1-red/20' : 'text-gray-600 hover:text-gray-400'}`}
                            >
                                <Car size={14} /> Konstrukteurs-WM
                            </button>
                        </div>
                        <button
                            onClick={() => { setCompareMode(!compareMode); setSelectedIds([]); }}
                            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 border transition-all ${compareMode ? 'border-yellow-500/50 text-yellow-500 bg-yellow-500/5' : 'border-gray-800/40 text-gray-600 hover:text-gray-400 bg-[#0e0e16]'}`}
                        >
                            <Swords size={13} /> {compareMode ? 'Beenden' : 'Vergleichen'}
                        </button>
                    </div>

                    {compareMode && (
                        <div className="bg-yellow-500/5 border border-yellow-500/20 px-4 py-2.5 rounded-lg text-center text-yellow-500 text-[10px] font-black uppercase tracking-wider">
                            Wähle 2 {tab === 'drivers' ? 'Fahrer' : 'Teams'} zum Vergleichen ({selectedIds.length}/2)
                        </div>
                    )}

                    {tab === 'constructors' && (
                        <div className="space-y-3">
                            {data.constructors.map((team, idx) => {
                                const selected = selectedIds.includes(team.id);
                                const leaderPoints = Math.max(Number(data.constructors[0]?.totalPoints || 0), 1);
                                const content = (
                                    <>
                                        <div className="flex w-14 shrink-0 items-center justify-center self-stretch border-r border-white/[0.06] font-mono text-xl font-black text-white/20 md:w-20">
                                            {idx < 3 ? <PosBadge pos={idx + 1} compareMode={compareMode} isSelected={selected} /> : String(idx + 1).padStart(2, '0')}
                                        </div>
                                        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[1.4rem] border border-white/[0.08] bg-black/25 p-3 shadow-xl md:h-24 md:w-24 md:p-4">
                                            {team.teamLogo ? <img src={getImageUrl(team.teamLogo)} alt={`${team.name} Logo`} className="max-h-full max-w-full object-contain drop-shadow-lg" /> : <Car size={24} className="text-white/15" />}
                                        </div>
                                        <div className="min-w-0 flex-1 py-5">
                                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                                <div className="min-w-0">
                                                    <p className="text-[8px] font-black uppercase tracking-[.2em]" style={{ color: team.teamColor || '#777' }}>Constructor</p>
                                                    <h3 className={`mt-1 truncate text-xl font-black uppercase italic tracking-tight transition md:text-2xl ${selected ? 'text-green-300' : 'text-white'}`}>{team.name}</h3>
                                                    <div className="mt-2 flex items-center gap-3 text-[9px] font-bold uppercase tracking-wider text-white/25"><span>{team.wins || 0} Siege</span><span>·</span><span className={team.lastRacePoints > 0 ? 'text-emerald-400' : ''}>{team.lastRacePoints > 0 ? `+${Math.round(team.lastRacePoints)} zuletzt` : 'Kein Zuwachs'}</span></div>
                                                </div>
                                                <div className="shrink-0 md:text-right"><p className="font-mono text-3xl font-black leading-none text-white md:text-4xl">{Math.round(team.totalPoints)}</p><p className="mt-1 text-[8px] font-black uppercase tracking-[.2em] text-white/25">Punkte</p></div>
                                            </div>
                                            <div className="mt-4 h-1 overflow-hidden rounded-full bg-white/[0.04]"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(2, Number(team.totalPoints) / leaderPoints * 100)}%`, backgroundColor: team.teamColor || '#777' }} /></div>
                                        </div>
                                        {team.carImageUrl && <div className="pointer-events-none hidden w-52 shrink-0 items-center justify-center overflow-hidden pr-5 lg:flex"><img src={getImageUrl(team.carImageUrl)} alt="" className="max-h-24 w-full object-contain object-right drop-shadow-[0_18px_18px_rgba(0,0,0,.5)] opacity-70 transition duration-500 group-hover:scale-105 group-hover:opacity-95" /></div>}
                                        <ChevronDown size={16} className="mr-5 -rotate-90 text-white/15" />
                                    </>
                                );

                                const rowClass = `group relative flex min-h-[122px] items-center gap-4 overflow-hidden rounded-[1.7rem] border bg-[#111218] transition duration-300 ${selected ? 'border-green-400/25 bg-green-400/[0.04]' : 'border-white/[0.07] hover:border-white/[0.15]'}`;
                                return compareMode
                                    ? <button key={team.id} onClick={() => handleRowClick(team.id)} className={`${rowClass} w-full text-left`}>{content}</button>
                                    : <Link key={team.id} to={`/team/${team.id}`} className={rowClass}>{content}</Link>;
                            })}
                        </div>
                    )}

                    {/* ── DRIVER TABLE ────────────────────────── */}
                    {tab === 'drivers' && <div className="overflow-hidden rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.23)]">
                        <div className="h-[2px] bg-gradient-to-r from-f1-red via-f1-red/50 to-transparent" />
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-[#0e0e16]">
                                        <th className="px-4 py-3 text-center w-14 text-[9px] font-bold text-gray-600 uppercase tracking-wider">Pos</th>
                                        <th className="px-4 py-3 text-[9px] font-bold text-gray-600 uppercase tracking-wider">{tab === 'drivers' ? 'Fahrer' : 'Team'}</th>
                                        {tab === 'drivers' && <th className="px-4 py-3 hidden md:table-cell text-[9px] font-bold text-gray-600 uppercase tracking-wider">Team</th>}
                                        <th className="px-4 py-3 text-center hidden sm:table-cell text-[9px] font-bold text-gray-600 uppercase tracking-wider">Siege</th>
                                        {tab === 'drivers' && <th className="px-4 py-3 text-center hidden md:table-cell text-[9px] font-bold text-gray-600 uppercase tracking-wider">Podien</th>}
                                        <th className="px-4 py-3 text-center hidden md:table-cell text-[9px] font-bold text-gray-600 uppercase tracking-wider">Trend</th>
                                        <th className="px-4 py-3 text-right text-[9px] font-bold text-gray-600 uppercase tracking-wider">Punkte</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(tab === 'drivers' ? data.drivers : data.constructors).map((row: any, idx) => {
                                        const isSelected = selectedIds.includes(row.id);
                                        const isReserve = !row.teamName || row.teamName === 'Reserve' || row.teamName === 'Privatfahrer';
                                        const logoToUse = (isReserve && tab === 'drivers' && settings.reserve_logo_url) ? settings.reserve_logo_url : row.teamLogo;

                                        const rowContent = (
                                            <>
                                                <td className="px-4 py-3 text-center">
                                                    <PosBadge pos={idx + 1} compareMode={compareMode} isSelected={isSelected} />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-1 h-8 rounded-full shrink-0" style={{ backgroundColor: row.teamColor || '#333' }} />
                                                        <div className="w-8 h-7 flex items-center justify-center shrink-0 bg-white/[0.03] rounded hidden sm:flex">
                                                            {logoToUse ? (
                                                                <img src={getImageUrl(logoToUse)} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                            ) : <Car className="text-gray-800 w-4 h-4" />}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className={`font-bold text-sm truncate transition-colors ${isSelected ? 'text-green-400' : 'text-white group-hover:text-f1-red'}`}>
                                                                {tab === 'drivers' ? row.username : row.name}
                                                            </div>
                                                            {tab === 'drivers' && <div className="text-[10px] text-gray-600 md:hidden uppercase font-bold truncate">{row.teamName}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                {tab === 'drivers' && <td className="px-4 py-3 hidden md:table-cell text-gray-500 text-xs font-bold">{row.teamName || 'Kein Team'}</td>}
                                                <td className="px-4 py-3 text-center hidden sm:table-cell font-mono text-sm">{row.wins > 0 ? <span className="text-white font-bold">{row.wins}</span> : <span className="text-gray-700">-</span>}</td>
                                                {tab === 'drivers' && <td className="px-4 py-3 text-center hidden md:table-cell font-mono text-sm">{row.podiums > 0 ? <span className="text-gray-300">{row.podiums}</span> : <span className="text-gray-700">-</span>}</td>}
                                                <td className="px-4 py-3 text-center hidden md:table-cell">
                                                    {row.lastRacePoints > 0 ? (
                                                        <span className="text-green-500 font-bold text-[10px] flex items-center justify-center gap-0.5"><TrendingUp size={10} /> +{Math.round(row.lastRacePoints)}</span>
                                                    ) : row.lastRacePoints < 0 ? (
                                                        <span className="text-red-500 font-bold text-[10px] flex items-center justify-center gap-0.5"><TrendingUp size={10} className="rotate-180" /> {Math.round(row.lastRacePoints)}</span>
                                                    ) : <span className="text-gray-800">-</span>}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex flex-col items-end">
                                                        <span className="font-mono font-black text-white text-sm tabular-nums">{Math.round(row.totalPoints)}</span>
                                                        <div className="md:hidden mt-0.5">
                                                            {row.lastRacePoints > 0 && <span className="text-green-500 font-bold text-[9px]">+{Math.round(row.lastRacePoints)}</span>}
                                                            {row.lastRacePoints < 0 && <span className="text-red-500 font-bold text-[9px]">{Math.round(row.lastRacePoints)}</span>}
                                                        </div>
                                                    </div>
                                                </td>
                                            </>
                                        );

                                        const trClass = `border-b border-gray-800/20 transition-colors ${idx < 3 ? 'bg-white/[0.01]' : ''} ${isSelected ? 'bg-green-900/10' : 'hover:bg-white/[0.02]'}`;
                                        
                                        if (compareMode) {
                                            return <tr key={row.id} onClick={() => handleRowClick(row.id)} className={`${trClass} cursor-pointer`}>{rowContent}</tr>;
                                        }
                                        return (
                                            <tr key={row.id} className={`${trClass} group`}>
                                                <Link to={tab === 'drivers' ? `/driver/${row.id}` : `/team/${row.id}`} className="contents" style={{ display: 'contents' }}>
                                                    {rowContent}
                                                </Link>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>}

                    {/* ── COMPARISON MODAL ────────────────────── */}
                    {compareMode && selectedIds.length === 2 && (
                        <ComparisonModal
                            item1={(tab === 'drivers' ? data.drivers : data.constructors).find(d => d.id === selectedIds[0])}
                            item2={(tab === 'drivers' ? data.drivers : data.constructors).find(d => d.id === selectedIds[1])}
                            type={tab === 'drivers' ? 'driver' : 'team'}
                            onClose={() => setSelectedIds([])}
                        />
                    )}
                </>
            )}
        </div>
    );
};
